import {
  PackingSlipTemplateKind,
  PackingSlipTemplateRecord,
} from './PackingSlipTemplate.interface'

export type PackingSlipProfilePayloadContext = {
  userAccountId?: number
  brandLogo: string
}

const LEGACY_STANDARD_JSON_KEYS = [
  'paperSize',
  'displayBrandLogo',
  'displayProductImages',
  'displayShippingMethod',
  'displayCustomerNotes',
  'displayWarehouseBins',
] as const

export const PACKING_SLIP_KIND_INFERENCE = {
  HTML_WITHOUT_TEMPLATE_KIND:
    'template_kind missing; template_data looks like HTML (starts with "<") — treated as custom',
  LEGACY_STANDARD_JSON:
    'template_kind missing; template_data parses as legacy Standard settings JSON',
  EXPLICIT_CUSTOM: 'template_kind is custom',
  EXPLICIT_STANDARD: 'template_kind is standard (or non-custom)',
  EMPTY_TEMPLATE_DATA: 'empty template_data — treated as standard',
  AMBIGUOUS_NON_JSON:
    'template_kind missing; template_data is non-empty, not legacy JSON, and not HTML — treated as standard (display_options / paper_size only)',
} as const

export const parsePackingSlipTemplateDataRaw = (templateData: unknown): string => {
  if (templateData === undefined || templateData === null) {
    return ''
  }
  return typeof templateData === 'string' ? templateData : String(templateData)
}

export const parsePackingSlipLegacyStandardSettings = (templateData: unknown) => {
  if (templateData === undefined || templateData === null) {
    return null
  }

  if (typeof templateData === 'object') {
    return templateData as Record<string, unknown>
  }

  if (typeof templateData !== 'string') {
    return null
  }

  const trimmed = templateData.trim()
  if (!trimmed || trimmed.startsWith('<')) {
    return null
  }

  try {
    const parsed = JSON.parse(trimmed)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

export const isPackingSlipLegacyStandardSettings = (
  parsed: Record<string, unknown> | null
): boolean => {
  if (!parsed) {
    return false
  }

  return LEGACY_STANDARD_JSON_KEYS.some((key) => key in parsed)
}

export type PackingSlipKindResolution = {
  kind: PackingSlipTemplateKind
  inferenceNote?: string
}

/**
 * Classifies a list API template row. When template_kind is absent, uses documented
 * fallbacks; ambiguous rows default to standard rather than assuming custom.
 */
export const resolvePackingSlipTemplateKindFromApi = (
  template: Record<string, unknown>
): PackingSlipKindResolution => {
  const rawKind = String(template.template_kind ?? template.templateKind ?? '')
    .trim()
    .toLowerCase()

  if (rawKind === 'custom') {
    return {kind: 'custom', inferenceNote: PACKING_SLIP_KIND_INFERENCE.EXPLICIT_CUSTOM}
  }

  if (rawKind === 'standard') {
    return {kind: 'standard', inferenceNote: PACKING_SLIP_KIND_INFERENCE.EXPLICIT_STANDARD}
  }

  const rawTemplateData = parsePackingSlipTemplateDataRaw(template.template_data)
  const trimmed = rawTemplateData.trim()

  if (!trimmed) {
    return {kind: 'standard', inferenceNote: PACKING_SLIP_KIND_INFERENCE.EMPTY_TEMPLATE_DATA}
  }

  const legacySettings = parsePackingSlipLegacyStandardSettings(template.template_data)
  if (isPackingSlipLegacyStandardSettings(legacySettings)) {
    return {kind: 'standard', inferenceNote: PACKING_SLIP_KIND_INFERENCE.LEGACY_STANDARD_JSON}
  }

  if (trimmed.startsWith('<')) {
    return {
      kind: 'custom',
      inferenceNote: PACKING_SLIP_KIND_INFERENCE.HTML_WITHOUT_TEMPLATE_KIND,
    }
  }

  return {
    kind: 'standard',
    inferenceNote: PACKING_SLIP_KIND_INFERENCE.AMBIGUOUS_NON_JSON,
  }
}

export const normalizePackingSlipPaperSizeFromApi = (paperSize: unknown): string => {
  if (paperSize === undefined || paperSize === null || String(paperSize).trim() === '') {
    return '4x6'
  }

  const normalized = String(paperSize).trim().toLowerCase()

  if (normalized === 'letter' || normalized === '8.5x11') {
    return 'letter'
  }

  if (normalized === 'a4') {
    return 'a4'
  }

  if (normalized.includes('4') && normalized.includes('6')) {
    return '4x6'
  }

  return normalized
}

/** Rahul's save example used title-case paper size labels for some formats. */
export const formatPackingSlipPaperSizeForCustomApi = (paperSize: string): string => {
  if (paperSize === 'letter') {
    return 'Letter'
  }

  if (paperSize === 'a4') {
    return 'A4'
  }

  return paperSize
}

export const parsePackingSlipTemplateAdditionalData = (
  value: unknown
): Record<string, unknown> => {
  if (!value) {
    return {}
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  if (typeof value !== 'string') {
    return {}
  }

  try {
    const parsed = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return parsed as Record<string, unknown>
  } catch {
    return {}
  }
}

const firstDefinedPackingSlipSetting = (...values: unknown[]): unknown =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== '')

export const packingSlipApiTemplateSpecifiesPaperSize = (
  template: Record<string, unknown>
): boolean => {
  const additionalData = parsePackingSlipTemplateAdditionalData(template.additional_data)

  return (
    firstDefinedPackingSlipSetting(
      template.paper_size,
      template.paperSize,
      additionalData.paper_size,
      additionalData.paperSize
    ) !== undefined
  )
}

export const resolvePackingSlipPaperSizeFromApiTemplate = (
  template: Record<string, unknown>,
  kind: PackingSlipTemplateKind,
  legacySettings: Record<string, unknown> | null
): string => {
  const additionalData = parsePackingSlipTemplateAdditionalData(template.additional_data)

  const source = firstDefinedPackingSlipSetting(
    kind === 'standard' ? legacySettings?.paperSize : undefined,
    template.paper_size,
    template.paperSize,
    additionalData.paper_size,
    additionalData.paperSize
  )

  return normalizePackingSlipPaperSizeFromApi(source)
}

/** Keeps custom layout settings when the save response omits fields the editor already had. */
export const mergePackingSlipTemplateRecordAfterSave = (
  apiRecord: PackingSlipTemplateRecord,
  savedDraft: PackingSlipTemplateRecord,
  rawApiTemplate?: Record<string, unknown> | null
): PackingSlipTemplateRecord => {
  if (savedDraft.templateKind !== 'custom') {
    return apiRecord
  }

  const preservePaperSize =
    Boolean(rawApiTemplate) && !packingSlipApiTemplateSpecifiesPaperSize(rawApiTemplate!)

  return {
    ...apiRecord,
    ...(preservePaperSize ? {paperSize: savedDraft.paperSize} : {}),
    orientation: apiRecord.orientation ?? savedDraft.orientation,
    marginMm: apiRecord.marginMm ?? savedDraft.marginMm,
    pdfFileName: apiRecord.pdfFileName ?? savedDraft.pdfFileName,
    repeatTableHeader: apiRecord.repeatTableHeader ?? savedDraft.repeatTableHeader,
    blockSavingUnknownSourceCode:
      apiRecord.blockSavingUnknownSourceCode ?? savedDraft.blockSavingUnknownSourceCode,
    templateHtml: apiRecord.templateHtml?.trim()
      ? apiRecord.templateHtml
      : savedDraft.templateHtml,
  }
}

const resolveExistingPackingSlipTemplateId = (templateOverride: PackingSlipTemplateRecord) =>
  templateOverride.templateId ??
  (!String(templateOverride.id).startsWith('new-packing-slip-') ? templateOverride.id : null)

/**
 * Standard packing slip save payload — must remain structurally identical to pre–Phase 2 behavior.
 */
export const buildStandardPackingSlipProfilePayload = (
  templateOverride: PackingSlipTemplateRecord,
  context: PackingSlipProfilePayloadContext
) => {
  const existingTemplateId = resolveExistingPackingSlipTemplateId(templateOverride)

  return {
    id: context.userAccountId,
    type: 'PACKING_SLIP',
    platform_ids: templateOverride.channels.map(Number).filter(Boolean),
    name: templateOverride.name,
    is_default: Boolean(templateOverride.isDefault),
    paper_size: templateOverride.paperSize,
    display_options: {
      display_brand_logo: Boolean(templateOverride.displayBrandLogo),
      display_product_images: Boolean(templateOverride.displayProductImages),
      display_product_images_in_table: Boolean(templateOverride.displayProductImages),
      display_shipping_method: Boolean(templateOverride.displayShippingMethod),
      display_shipping_method_carrier: Boolean(templateOverride.displayShippingMethod),
      display_shipping_method_and_carrier: Boolean(templateOverride.displayShippingMethod),
      display_customer_checkout_notes: Boolean(templateOverride.displayCustomerNotes),
      display_warehouse_bin_locations: Boolean(templateOverride.displayWarehouseBins),
    },
    ...(existingTemplateId ? {template_id: existingTemplateId} : {}),
    template_data: JSON.stringify({
      paperSize: templateOverride.paperSize,
      displayBrandLogo: templateOverride.displayBrandLogo,
      displayProductImages: templateOverride.displayProductImages,
      displayShippingMethod: templateOverride.displayShippingMethod,
      displayCustomerNotes: templateOverride.displayCustomerNotes,
      displayWarehouseBins: templateOverride.displayWarehouseBins,
    }),
    brand_logo: templateOverride.logoUrl || context.brandLogo,
    logo: templateOverride.logoUrl || context.brandLogo,
  }
}

export const buildCustomPackingSlipProfilePayload = (
  templateOverride: PackingSlipTemplateRecord,
  context: PackingSlipProfilePayloadContext
) => {
  const existingTemplateId = resolveExistingPackingSlipTemplateId(templateOverride)

  return {
    id: context.userAccountId,
    type: 'PACKING_SLIP',
    platform_ids: templateOverride.channels.map(Number).filter(Boolean),
    name: templateOverride.name,
    is_default: Boolean(templateOverride.isDefault),
    template_kind: 'custom',
    paper_size: formatPackingSlipPaperSizeForCustomApi(templateOverride.paperSize),
    orientation: templateOverride.orientation ?? 'portrait',
    margin_mm: templateOverride.marginMm ?? 0,
    pdf_file_name: templateOverride.pdfFileName ?? '',
    repeat_table_header: Boolean(templateOverride.repeatTableHeader),
    block_saving_unknown_source_code: Boolean(
      templateOverride.blockSavingUnknownSourceCode
    ),
    display_options: {
      brand_logo: Boolean(templateOverride.displayBrandLogo),
      product_images: Boolean(templateOverride.displayProductImages),
      shipping_method_carrier: Boolean(templateOverride.displayShippingMethod),
      customer_checkout_notes: Boolean(templateOverride.displayCustomerNotes),
      warehouse_bin_locations: Boolean(templateOverride.displayWarehouseBins),
    },
    ...(existingTemplateId ? {template_id: existingTemplateId} : {}),
    template_data: templateOverride.templateHtml ?? '',
  }
}

export const buildPackingSlipProfilePayloadFromRecord = (
  templateOverride: PackingSlipTemplateRecord,
  context: PackingSlipProfilePayloadContext
) => {
  if (templateOverride.templateKind === 'custom') {
    return buildCustomPackingSlipProfilePayload(templateOverride, context)
  }

  return buildStandardPackingSlipProfilePayload(templateOverride, context)
}

export const mapApiPackingSlipTemplateToRecord = (
  template: Record<string, unknown>,
  index: number
): PackingSlipTemplateRecord => {
  const displayOptions =
    template.display_options && typeof template.display_options === 'object'
      ? (template.display_options as Record<string, unknown>)
      : {}

  const {kind} = resolvePackingSlipTemplateKindFromApi(template)
  const rawTemplateData = parsePackingSlipTemplateDataRaw(template.template_data)
  const legacySettings =
    kind === 'standard' ? parsePackingSlipLegacyStandardSettings(template.template_data) : null
  const additionalData = parsePackingSlipTemplateAdditionalData(template.additional_data)

  const readDisplayFlag = (
    legacyKey: keyof PackingSlipTemplateRecord,
    customOptionKey: string,
    standardOptionKey: string,
    fallback = true
  ) => {
    if (legacySettings && legacyKey in legacySettings) {
      return Boolean(legacySettings[legacyKey as string])
    }

    if (kind === 'custom' && customOptionKey in displayOptions) {
      return Boolean(displayOptions[customOptionKey])
    }

    if (standardOptionKey in displayOptions) {
      return Boolean(displayOptions[standardOptionKey])
    }

    if (legacyKey in template) {
      return Boolean(template[legacyKey as string])
    }

    return fallback
  }

  const paperSize = resolvePackingSlipPaperSizeFromApiTemplate(template, kind, legacySettings)

  const templateHtml = rawTemplateData

  const marginSource = firstDefinedPackingSlipSetting(
    template.margin_mm,
    template.marginMm,
    additionalData.margin_mm,
    additionalData.marginMm
  )
  const marginMm =
    marginSource !== undefined ? Number(marginSource) : undefined

  const orientationSource = firstDefinedPackingSlipSetting(
    template.orientation,
    additionalData.orientation
  )
  const orientation =
    typeof orientationSource === 'string' ? orientationSource : undefined

  const pdfFileNameSource = firstDefinedPackingSlipSetting(
    template.pdf_file_name,
    template.pdfFileName,
    additionalData.pdf_file_name,
    additionalData.pdfFileName
  )
  const pdfFileName =
    typeof pdfFileNameSource === 'string' ? pdfFileNameSource : undefined

  const repeatTableHeaderSource = firstDefinedPackingSlipSetting(
    template.repeat_table_header,
    template.repeatTableHeader,
    additionalData.repeat_table_header,
    additionalData.repeatTableHeader
  )
  const repeatTableHeader =
    repeatTableHeaderSource !== undefined ? Boolean(repeatTableHeaderSource) : undefined

  const blockSavingUnknownSourceCodeSource = firstDefinedPackingSlipSetting(
    template.block_saving_unknown_source_code,
    template.blockSavingUnknownSourceCode,
    additionalData.block_saving_unknown_source_code,
    additionalData.blockSavingUnknownSourceCode
  )
  const blockSavingUnknownSourceCode =
    blockSavingUnknownSourceCodeSource !== undefined
      ? Boolean(blockSavingUnknownSourceCodeSource)
      : undefined

  const logoUrl =
    [template.brand_logo, template.logo].find(
      (value) => value !== undefined && value !== null && String(value).trim() !== ''
    ) ?? ''

  return {
    id: String(template.id ?? `packing-slip-${index}`),
    templateId: (template.template_id ?? template.id ?? null) as number | string | null,
    name:
      typeof template.name === 'string'
        ? template.name
        : `Packing Slip Template #${template.id ?? index + 1}`,
    channels: Array.isArray(template.platform_ids)
      ? template.platform_ids.map(String)
      : [],
    templateKind: kind,
    paperSize,
    displayBrandLogo: readDisplayFlag(
      'displayBrandLogo',
      'brand_logo',
      'display_brand_logo'
    ),
    displayProductImages: readDisplayFlag(
      'displayProductImages',
      'product_images',
      'display_product_images'
    ),
    displayShippingMethod: readDisplayFlag(
      'displayShippingMethod',
      'shipping_method_carrier',
      'display_shipping_method'
    ),
    displayCustomerNotes: readDisplayFlag(
      'displayCustomerNotes',
      'customer_checkout_notes',
      'display_customer_checkout_notes'
    ),
    displayWarehouseBins: readDisplayFlag(
      'displayWarehouseBins',
      'warehouse_bin_locations',
      'display_warehouse_bin_locations'
    ),
    isDefault: Boolean(template.is_default),
    templateHtml,
    logoUrl: String(logoUrl),
    ...(marginMm !== undefined && Number.isFinite(marginMm) ? {marginMm} : {}),
    ...(orientation ? {orientation} : {}),
    ...(pdfFileName !== undefined ? {pdfFileName} : {}),
    ...(repeatTableHeader !== undefined ? {repeatTableHeader} : {}),
    ...(blockSavingUnknownSourceCode !== undefined
      ? {blockSavingUnknownSourceCode}
      : {}),
  }
}

export type NormalizedPackingSlipShortcode = {
  shortcode: string
  label?: string
  description?: string
  /** Preview/sample value for the shortcodes panel (from API example fields when present). */
  exampleValue?: string
  group: string
  groupLabel: string
}

export type NormalizedPackingSlipDefaultTemplate = {
  id: string
  label: string
  html: string
  isDefault?: boolean
}

export const describePackingSlipApiPayloadShape = (payload: unknown): string => {
  if (payload === null || payload === undefined) {
    return 'empty response'
  }

  if (Array.isArray(payload)) {
    return `array length ${payload.length}`
  }

  if (typeof payload === 'object') {
    return `object keys: ${Object.keys(payload as Record<string, unknown>).join(', ')}`
  }

  return `type ${typeof payload}`
}

export const summarizePackingSlipDefaultTemplatesPayload = (payload: unknown): string => {
  const list = unwrapPackingSlipApiListResponse(payload)

  if (!list.length) {
    return describePackingSlipApiPayloadShape(payload)
  }

  const firstItem = list[0]
  if (firstItem && typeof firstItem === 'object') {
    return `list length ${list.length}, first item keys: ${Object.keys(
      firstItem as Record<string, unknown>
    ).join(', ')}`
  }

  return `list length ${list.length}`
}

export const pickInitialPackingSlipDefaultTemplate = (
  templates: NormalizedPackingSlipDefaultTemplate[]
): NormalizedPackingSlipDefaultTemplate | null => {
  const withHtml = templates.filter((template) => template.html.trim())

  if (!withHtml.length) {
    return null
  }

  return withHtml.find((template) => template.isDefault) ?? withHtml[0]
}

const PACKING_SLIP_SHORTCODE_GROUP_LABELS: Record<string, string> = {
  ORDER: 'Order',
  BUYER: 'Buyer',
  ADDRESSES: 'Addresses',
  SHIPMENT: 'Shipping',
  LINE_ITEMS: 'Products',
  STORE: 'Your business',
  BARCODES: 'Barcodes & extras',
  BARCODES_AND_LOGIC: 'Barcodes & extras',
  BARCODES_LOGIC: 'Barcodes & extras',
}

export const unwrapPackingSlipApiListResponse = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>

  if (Array.isArray(record.results)) {
    return record.results
  }

  if (Array.isArray(record.data)) {
    return record.data
  }

  const nestedResults = record.results
  if (
    nestedResults &&
    typeof nestedResults === 'object' &&
    Array.isArray((nestedResults as Record<string, unknown>).data)
  ) {
    return (nestedResults as Record<string, unknown>).data as unknown[]
  }

  return []
}

/** Maps GET /api/user-email-templates?type=PACKING_SLIP payloads into editor records (post-save list refresh). */
export const mapPackingSlipEmailTemplatesListResponse = (
  payload: unknown
): PackingSlipTemplateRecord[] =>
  unwrapPackingSlipApiListResponse(payload).map((template, index) =>
    mapApiPackingSlipTemplateToRecord(
      template && typeof template === 'object' ? (template as Record<string, unknown>) : {},
      index
    )
  )

export const formatPackingSlipShortcodeGroupLabel = (group: string): string => {
  const normalized = group.trim().toUpperCase()
  if (!normalized) {
    return 'Other'
  }

  if (PACKING_SLIP_SHORTCODE_GROUP_LABELS[normalized]) {
    return PACKING_SLIP_SHORTCODE_GROUP_LABELS[normalized]
  }

  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ')
}

const PACKING_SLIP_SHORTCODE_GROUP_CONTAINER_KEYS = new Set([
  ...Object.keys(PACKING_SLIP_SHORTCODE_GROUP_LABELS),
  'LINE_ITEM',
])

const normalizePackingSlipShortcodeGroupName = (group: string): string => {
  if (typeof group !== 'string' || !group.trim()) {
    return 'OTHER'
  }

  const normalized = group.trim().toUpperCase()
  if (normalized === 'LINE_ITEM') {
    return 'LINE_ITEMS'
  }

  return normalized
}

const resolvePackingSlipShortcodeGroup = (item: Record<string, unknown>): string => {
  const rawGroup = item.shortcode_group ?? item.group ?? item.category
  if (typeof rawGroup === 'string' && rawGroup.trim()) {
    return normalizePackingSlipShortcodeGroupName(rawGroup)
  }

  return 'OTHER'
}

const resolvePackingSlipShortcodeGroupFromEntryKey = (
  entryKey: string,
  parentGroup: string
): string => {
  const normalizedKey = entryKey.trim().toUpperCase()
  if (PACKING_SLIP_SHORTCODE_GROUP_CONTAINER_KEYS.has(normalizedKey)) {
    return normalizePackingSlipShortcodeGroupName(normalizedKey)
  }

  return parentGroup
}

const isPackingSlipShortcodeGroupContainerKey = (entryKey: string): boolean =>
  PACKING_SLIP_SHORTCODE_GROUP_CONTAINER_KEYS.has(entryKey.trim().toUpperCase())

/** True for Liquid tags, bracket tokens, or dotted template paths — not labels or sample values. */
export const looksLikePackingSlipShortcodeToken = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) {
    return false
  }

  if (trimmed.startsWith('{{') || trimmed.startsWith('{%')) {
    return true
  }

  if (/^\[[A-Z][A-Z0-9_]*\]$/.test(trimmed)) {
    return true
  }

  return /^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*)+$/.test(trimmed)
}

const resolvePackingSlipShortcodeToken = (item: Record<string, unknown>): string | null => {
  const candidates = [
    item.shortcode,
    item.source_code,
    item.expression,
    item.liquid,
    item.name,
    item.code,
    item.token,
    item.key,
    item.value,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && looksLikePackingSlipShortcodeToken(candidate)) {
      return candidate.trim()
    }
  }

  return null
}

const resolvePackingSlipShortcodeExampleValue = (
  item: Record<string, unknown>
): string | undefined => {
  const candidates = [
    item.example_value,
    item.example,
    item.sample_value,
    item.sample,
    item.preview_value,
    item.mock_value,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  return undefined
}

/** Bare catalogue keys (e.g. `logo_url`) → dotted Liquid paths used in starter HTML (`store.logo_url`). */
const PACKING_SLIP_SHORTCODE_GROUP_EXPRESSION_PREFIX: Partial<Record<string, string>> = {
  ORDER: 'order.',
  BUYER: 'customer.',
  SHIPMENT: 'shipment.',
  LINE_ITEMS: 'item.',
  STORE: 'store.',
}

const isBarePackingSlipShortcodeIdentifier = (value: string): boolean =>
  /^[A-Za-z_]\w*$/.test(value.trim())

const collectLiquidTokensFromPackingSlipCatalogItem = (
  item: Record<string, unknown>
): string[] => {
  const tokens = new Set<string>()

  for (const value of Object.values(item)) {
    if (typeof value !== 'string' || !value.trim()) {
      continue
    }

    if (!value.includes('{{') && !value.includes('{%')) {
      continue
    }

    extractPackingSlipShortcodeCandidates(value).forEach((candidate) => tokens.add(candidate))
  }

  return Array.from(tokens)
}

const resolvePackingSlipCatalogItemShortcodeToken = (
  item: Record<string, unknown>,
  group: string
): string | null => {
  const primary = resolvePackingSlipShortcodeToken(item)
  if (primary) {
    return primary
  }

  const liquidTokens = collectLiquidTokensFromPackingSlipCatalogItem(item)
  if (liquidTokens.length) {
    return liquidTokens[0]
  }

  for (const field of ['key', 'field', 'field_name', 'code', 'token', 'name'] as const) {
    const raw = item[field]
    if (typeof raw !== 'string' || !isBarePackingSlipShortcodeIdentifier(raw.trim())) {
      continue
    }

    const prefix = PACKING_SLIP_SHORTCODE_GROUP_EXPRESSION_PREFIX[group]
    if (prefix) {
      return `${prefix}${raw.trim()}`
    }
  }

  return null
}

const registerPackingSlipShortcodeCatalogEntry = (
  shortcodes: Map<string, NormalizedPackingSlipShortcode>,
  group: string,
  shortcode: string,
  metadata?: Pick<NormalizedPackingSlipShortcode, 'label' | 'description' | 'exampleValue'>
): void => {
  shortcodes.set(`${group}:${shortcode}`, {
    shortcode,
    label: metadata?.label,
    description: metadata?.description,
    exampleValue: metadata?.exampleValue,
    group,
    groupLabel: formatPackingSlipShortcodeGroupLabel(group),
  })
}

/** Unwrap common envelopes for GET /api/packing-slip-shortcodes before tree parsing. */
export const unwrapPackingSlipShortcodesResponsePayload = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') {
    return payload
  }

  const record = payload as Record<string, unknown>

  if (record.shortcodes !== undefined) {
    return record.shortcodes
  }

  if (record.data && typeof record.data === 'object') {
    const data = record.data as Record<string, unknown>
    if (data.shortcodes !== undefined) {
      return data.shortcodes
    }
  }

  return payload
}

export const inferPackingSlipShortcodeGroupFromExpression = (expression: string): string => {
  const root = expression.split('.')[0]?.trim().toLowerCase()
  const rootToGroup: Record<string, string> = {
    store: 'STORE',
    item: 'LINE_ITEMS',
    order: 'ORDER',
    customer: 'BUYER',
    buyer: 'BUYER',
    shipment: 'SHIPMENT',
    shipping: 'ADDRESSES',
    billing: 'ADDRESSES',
    warehouse: 'ADDRESSES',
  }

  return rootToGroup[root] ?? 'OTHER'
}

/**
 * Adds Liquid tokens present in backend default starter HTML when the shortcode catalogue
 * omits them. Uses the same default-templates API the editor already loads — not a static whitelist.
 */
export const augmentPackingSlipShortcodeCatalogWithDefaultTemplates = (
  shortcodes: NormalizedPackingSlipShortcode[],
  defaultTemplates: NormalizedPackingSlipDefaultTemplate[]
): NormalizedPackingSlipShortcode[] => {
  if (!shortcodes.length || !defaultTemplates.length) {
    return shortcodes
  }

  const lookup = buildKnownPackingSlipShortcodeLookup(shortcodes)
  const augmented = [...shortcodes]
  const seenKeys = new Set(shortcodes.map((entry) => `${entry.group}:${entry.shortcode}`))

  for (const template of defaultTemplates) {
    const html = template.html.trim()
    if (!html) {
      continue
    }

    for (const candidate of extractPackingSlipShortcodeCandidates(html)) {
      if (isPackingSlipLiquidControlTag(candidate)) {
        continue
      }

      if (isPackingSlipShortcodeKnownInLookup(candidate, lookup)) {
        continue
      }

      const expression = packingSlipShortcodeExpressionKey(candidate)
      if (!expression) {
        continue
      }

      const group = inferPackingSlipShortcodeGroupFromExpression(expression)
      const shortcode = normalizePackingSlipShortcodeForCatalogLookup(
        normalizePackingSlipShortcodeToken(candidate)
      )
      const dedupeKey = `${group}:${shortcode}`
      if (seenKeys.has(dedupeKey)) {
        continue
      }

      seenKeys.add(dedupeKey)
      augmented.push({
        shortcode,
        group,
        groupLabel: formatPackingSlipShortcodeGroupLabel(group),
        label: `Starter template: ${template.label}`,
      })
      registerPackingSlipShortcodeLookupVariants(lookup, shortcode)
    }
  }

  return augmented
}

export const parsePackingSlipShortcodesResponse = (
  payload: unknown
): NormalizedPackingSlipShortcode[] => {
  const shortcodes = new Map<string, NormalizedPackingSlipShortcode>()

  const visit = (value: unknown, inheritedGroup = 'OTHER') => {
    if (Array.isArray(value)) {
      value.forEach((entry) => visit(entry, inheritedGroup))
      return
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) {
        return
      }

      if (looksLikePackingSlipShortcodeToken(trimmed)) {
        registerPackingSlipShortcodeCatalogEntry(shortcodes, inheritedGroup, trimmed)
      }
      return
    }

    if (!value || typeof value !== 'object') {
      return
    }

    const item = value as Record<string, unknown>
    const resolvedGroup = resolvePackingSlipShortcodeGroup(item)
    const group =
      resolvedGroup !== 'OTHER'
        ? resolvedGroup
        : normalizePackingSlipShortcodeGroupName(inheritedGroup)
    const token = resolvePackingSlipCatalogItemShortcodeToken(item, group)

    if (token) {
      const exampleValue = resolvePackingSlipShortcodeExampleValue(item)
      const description =
        typeof item.description === 'string' && item.description.trim()
          ? item.description.trim()
          : undefined

      registerPackingSlipShortcodeCatalogEntry(shortcodes, group, token, {
        label: typeof item.label === 'string' ? item.label : undefined,
        description,
        exampleValue: exampleValue ?? description,
      })
      return
    }

    Object.entries(item).forEach(([entryKey, entryValue]) => {
      const childGroup = resolvePackingSlipShortcodeGroupFromEntryKey(entryKey, group)

      if (looksLikePackingSlipShortcodeToken(entryKey)) {
        registerPackingSlipShortcodeCatalogEntry(shortcodes, group, entryKey)
      } else if (
        isBarePackingSlipShortcodeIdentifier(entryKey) &&
        !isPackingSlipShortcodeGroupContainerKey(entryKey)
      ) {
        const prefix = PACKING_SLIP_SHORTCODE_GROUP_EXPRESSION_PREFIX[group]
        if (prefix) {
          registerPackingSlipShortcodeCatalogEntry(shortcodes, group, `${prefix}${entryKey}`)
        }
      }

      visit(entryValue, childGroup)
    })
  }

  visit(unwrapPackingSlipShortcodesResponsePayload(payload))

  return Array.from(shortcodes.values())
}

export const parsePackingSlipDefaultTemplatesResponse = (
  payload: unknown
): NormalizedPackingSlipDefaultTemplate[] => {
  return unwrapPackingSlipApiListResponse(payload)
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const html = [
        record.template_data,
        record.template_html,
        record.html,
        record.content,
        record.body,
      ].find((value) => typeof value === 'string')

      const label = [record.name, record.title, record.label].find(
        (value) => typeof value === 'string' && value.trim()
      )

      if (!label) {
        return null
      }

      const id = String(record.id ?? record.slug ?? `default-template-${index}`)
      const isDefault =
        record.is_default !== undefined ? Boolean(record.is_default) : undefined

      return {
        id,
        label: String(label),
        html: typeof html === 'string' ? html : '',
        ...(isDefault !== undefined ? {isDefault} : {}),
      }
    })
    .filter((item): item is NormalizedPackingSlipDefaultTemplate => item !== null)
}

/** Collapses whitespace inside Liquid-style tokens for catalogue comparison. */
const PACKING_SLIP_LIQUID_CONTROL_TAG_KEYWORDS = new Set([
  'assign',
  'break',
  'capture',
  'case',
  'comment',
  'continue',
  'cycle',
  'decrement',
  'doc',
  'echo',
  'else',
  'elsif',
  'endassign',
  'endcapture',
  'endcase',
  'endcomment',
  'enddoc',
  'endfor',
  'endunless',
  'endif',
  'endjavascript',
  'endpaginate',
  'endraw',
  'endstyle',
  'endtablerow',
  'endif',
  'for',
  'if',
  'include',
  'increment',
  'javascript',
  'layout',
  'liquid',
  'paginate',
  'raw',
  'render',
  'style',
  'tablerow',
  'unless',
  'when',
])

/** True for Liquid control/logic tags (`{% for %}`, `{% endif %}`, etc.), not data shortcodes. */
export const isPackingSlipLiquidControlTag = (token: string): boolean => {
  const trimmed = token.trim()
  const tagMatch = trimmed.match(/^\{%\s*([\w-]+)/i)
  if (!tagMatch) {
    return false
  }

  const keyword = tagMatch[1].toLowerCase()
  return PACKING_SLIP_LIQUID_CONTROL_TAG_KEYWORDS.has(keyword)
}

/** Strips Liquid output filters so `{{ order.number | barcode }}` can match catalogue `{{ order.number }}`. */
export const normalizePackingSlipShortcodeForCatalogLookup = (token: string): string => {
  const normalized = normalizePackingSlipShortcodeToken(token)
  const outputMatch = normalized.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/)

  if (!outputMatch) {
    return normalized
  }

  const expression = outputMatch[1].split('|')[0]?.trim().replace(/\s+/g, ' ')
  if (!expression) {
    return normalized
  }

  return `{{ ${expression} }}`
}

export const normalizePackingSlipShortcodeToken = (token: string): string => {
  const trimmed = token.trim()

  const liquidOutput = trimmed.match(/^(\{\{)\s*([\s\S]*?)\s*(\}\})$/)
  if (liquidOutput) {
    const inner = liquidOutput[2].trim().replace(/\s+/g, ' ')
    return `{{ ${inner} }}`
  }

  const liquidTag = trimmed.match(/^(\{%)\s*([\s\S]*?)\s*(%\})$/)
  if (liquidTag) {
    const inner = liquidTag[2].trim().replace(/\s+/g, ' ')
    return `{% ${inner} %}`
  }

  return trimmed
}

/** Liquid output expression without filters, e.g. `order.number` from `{{ order.number | barcode }}`. */
export const packingSlipShortcodeExpressionKey = (token: string): string | null => {
  const catalogKey = normalizePackingSlipShortcodeForCatalogLookup(
    normalizePackingSlipShortcodeToken(token)
  )
  const outputMatch = catalogKey.match(/^\{\{\s*([\s\S]+?)\s*\}\}$/)

  if (outputMatch) {
    const expression = outputMatch[1].trim().replace(/\s+/g, ' ')
    return expression || null
  }

  const trimmed = token.trim()
  if (!trimmed || trimmed.startsWith('{%') || trimmed.startsWith('{{')) {
    return null
  }

  if (trimmed.includes('.')) {
    return trimmed.replace(/\s+/g, ' ')
  }

  return null
}

const registerPackingSlipShortcodeLookupVariants = (
  lookup: Set<string>,
  token: string
): void => {
  const trimmed = token.trim()
  if (!trimmed) {
    return
  }

  lookup.add(trimmed)
  lookup.add(normalizePackingSlipShortcodeToken(trimmed))
  lookup.add(normalizePackingSlipShortcodeForCatalogLookup(trimmed))

  const expression = packingSlipShortcodeExpressionKey(trimmed)
  if (expression) {
    lookup.add(expression)
    lookup.add(expression.toLowerCase())
    lookup.add(`{{ ${expression} }}`)
    lookup.add(`{{${expression}}}`)
  }
}

export const buildKnownPackingSlipShortcodeLookup = (
  shortcodes: NormalizedPackingSlipShortcode[]
): Set<string> => {
  const lookup = new Set<string>()

  for (const entry of shortcodes) {
    registerPackingSlipShortcodeLookupVariants(lookup, entry.shortcode)

    const expression =
      packingSlipShortcodeExpressionKey(entry.shortcode) ??
      (isBarePackingSlipShortcodeIdentifier(entry.shortcode) ? entry.shortcode : null)

    if (expression && !expression.includes('.')) {
      const prefix = PACKING_SLIP_SHORTCODE_GROUP_EXPRESSION_PREFIX[entry.group]
      if (prefix) {
        registerPackingSlipShortcodeLookupVariants(lookup, `${prefix}${expression}`)
      }
    }
  }

  return lookup
}

export const isPackingSlipShortcodeKnownInLookup = (
  candidate: string,
  lookup: Set<string>
): boolean => {
  if (lookup.has(candidate)) {
    return true
  }

  const normalized = normalizePackingSlipShortcodeToken(candidate)
  if (lookup.has(normalized)) {
    return true
  }

  const catalogKey = normalizePackingSlipShortcodeForCatalogLookup(candidate)
  if (lookup.has(catalogKey)) {
    return true
  }

  const expression = packingSlipShortcodeExpressionKey(candidate)
  if (!expression) {
    return false
  }

  return lookup.has(expression) || lookup.has(expression.toLowerCase())
}

/**
 * Extracts shortcode-like tokens from template HTML.
 * Script and style blocks are skipped to reduce false positives from CSS/JS.
 */
export const extractPackingSlipShortcodeCandidates = (html: string): string[] => {
  const scanTarget = html
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    .replace(/<style\b[\s\S]*?<\/style>/gi, '')

  const found = new Set<string>()
  const patterns = [/\{\{[\s\S]*?\}\}/g, /\{%[\s\S]*?%\}/g, /\[[A-Z][A-Z0-9_]*\]/g]

  for (const pattern of patterns) {
    let match = pattern.exec(scanTarget)
    while (match) {
      found.add(match[0])
      match = pattern.exec(scanTarget)
    }
  }

  return Array.from(found)
}

export type ValidatePackingSlipShortcodesResult = {
  unknownTokens: string[]
  /** False when the shortcode catalogue is empty or failed to load. */
  catalogLoaded: boolean
}

/** Shortcode validation state reported from the custom HTML workspace (includes fetch lifecycle). */
export type PackingSlipShortcodeValidationState = ValidatePackingSlipShortcodesResult & {
  /** True after the catalogue fetch attempt has finished (success or failure). */
  catalogCheckComplete: boolean
}

export const isPackingSlipLegacyStandardTemplateData = (templateData: unknown): boolean => {
  const legacy = parsePackingSlipLegacyStandardSettings(templateData)
  return isPackingSlipLegacyStandardSettings(legacy)
}

/** True when template_data / editor HTML is non-empty and not legacy Standard JSON. */
export const hasPackingSlipCustomHtmlBody = (templateHtml: string | undefined): boolean => {
  const trimmed = (templateHtml ?? '').trim()
  if (!trimmed) {
    return false
  }

  return !isPackingSlipLegacyStandardTemplateData(trimmed)
}

export type PackingSlipTemplateKindSwitchPlan = {
  requiresConfirmation: boolean
  title: string
  message: string
  patch: Partial<PackingSlipTemplateRecord>
}

export const planPackingSlipTemplateKindSwitch = (
  record: PackingSlipTemplateRecord,
  nextKind: PackingSlipTemplateKind
): PackingSlipTemplateKindSwitchPlan | null => {
  if (record.templateKind === nextKind) {
    return null
  }

  const html = (record.templateHtml ?? '').trim()
  const isLegacyJson = isPackingSlipLegacyStandardTemplateData(html)
  const hasCustomBody = hasPackingSlipCustomHtmlBody(record.templateHtml)

  if (record.templateKind === 'standard' && nextKind === 'custom') {
    if (isLegacyJson) {
      return {
        requiresConfirmation: true,
        title: 'Switch to Custom HTML?',
        message:
          'This template currently stores standard layout settings as JSON, not HTML. Switching will clear that stored data from the editor so you can use custom HTML. Paper size and other fields on the left are kept until you save.',
        patch: {templateKind: 'custom', templateHtml: ''},
      }
    }

    return {
      requiresConfirmation: false,
      title: '',
      message: '',
      patch: {templateKind: 'custom'},
    }
  }

  if (record.templateKind === 'custom' && nextKind === 'standard') {
    if (hasCustomBody) {
      return {
        requiresConfirmation: true,
        title: 'Switch to Standard?',
        message:
          'Your custom HTML remains in this session if you switch back to Custom HTML. Saving while Standard is selected will store standard layout settings instead of your HTML.',
        patch: {templateKind: 'standard'},
      }
    }

    return {
      requiresConfirmation: false,
      title: '',
      message: '',
      patch: {templateKind: 'standard'},
    }
  }

  return {
    requiresConfirmation: false,
    title: '',
    message: '',
    patch: {templateKind: nextKind},
  }
}

export const shouldBlockPackingSlipSaveForShortcodeValidation = (
  blockSavingUnknownSourceCode: boolean,
  state: PackingSlipShortcodeValidationState
): boolean => {
  if (!blockSavingUnknownSourceCode) {
    return false
  }

  if (!state.catalogCheckComplete) {
    return true
  }

  if (!state.catalogLoaded) {
    return true
  }

  return state.unknownTokens.length > 0
}

export const packingSlipShortcodeSaveBlockMessage = (
  blockSavingUnknownSourceCode: boolean,
  state: PackingSlipShortcodeValidationState
): string | null => {
  if (!shouldBlockPackingSlipSaveForShortcodeValidation(blockSavingUnknownSourceCode, state)) {
    return null
  }

  if (!state.catalogCheckComplete) {
    return 'Wait for the shortcode catalogue to finish loading before saving.'
  }

  if (!state.catalogLoaded) {
    return 'Shortcode catalogue could not be loaded. Saving is blocked until shortcodes can be verified.'
  }

  if (state.unknownTokens.length) {
    return 'Remove or replace unknown shortcodes before saving this custom template.'
  }

  return null
}

export const validatePackingSlipTemplateShortcodes = (
  html: string,
  shortcodes: NormalizedPackingSlipShortcode[]
): ValidatePackingSlipShortcodesResult => {
  if (!shortcodes.length) {
    return {unknownTokens: [], catalogLoaded: false}
  }

  const lookup = buildKnownPackingSlipShortcodeLookup(shortcodes)
  const unknownTokens = extractPackingSlipShortcodeCandidates(html).filter((candidate) => {
    if (isPackingSlipLiquidControlTag(candidate)) {
      return false
    }

    return !isPackingSlipShortcodeKnownInLookup(candidate, lookup)
  })

  return {unknownTokens, catalogLoaded: true}
}

export type PackingSlipCustomPreviewDocumentOptions = {
  html: string
  paperWidth: string
  paperHeight: string
  marginMm?: number
  /** Used for preview viewport meta (CSS px). Defaults from `paperWidth` when omitted. */
  paperWidthPx?: number
}

/** Preview canvas dimensions (full sheet), keyed by normalized `paperSize`. */
export const PACKING_SLIP_PREVIEW_PAGE_FORMATS: Record<
  string,
  {width: string; height: string}
> = {
  '4x6': {width: '4in', height: '6in'},
  a4: {width: '8.27in', height: '11.69in'},
  letter: {width: '8.5in', height: '11in'},
}

export const resolvePackingSlipPreviewPaperDimensions = (
  paperSize: unknown,
  orientation: unknown
): {paperWidth: string; paperHeight: string; paperSizeKey: string} => {
  const paperSizeKey = normalizePackingSlipPaperSizeFromApi(paperSize)
  const format =
    PACKING_SLIP_PREVIEW_PAGE_FORMATS[paperSizeKey] ??
    PACKING_SLIP_PREVIEW_PAGE_FORMATS.letter

  const isLandscape =
    String(orientation ?? 'portrait').trim().toLowerCase() === 'landscape'

  return {
    paperSizeKey,
    paperWidth: isLandscape ? format.height : format.width,
    paperHeight: isLandscape ? format.width : format.height,
  }
}

const PACKING_SLIP_CSS_PX_PER_INCH = 96

export const packingSlipCssLengthToPx = (value: string): number => {
  const match = value.trim().match(/^([\d.]+)(in|mm|px)$/i)
  if (!match) {
    return PACKING_SLIP_CSS_PX_PER_INCH * 8.5
  }

  const amount = Number.parseFloat(match[1])
  const unit = match[2].toLowerCase()

  if (unit === 'in') {
    return amount * PACKING_SLIP_CSS_PX_PER_INCH
  }

  if (unit === 'mm') {
    return amount * (PACKING_SLIP_CSS_PX_PER_INCH / 25.4)
  }

  return amount
}

/** Portion of the preview stage width occupied by the rendered sheet. */
export const PACKING_SLIP_CUSTOM_PREVIEW_STAGE_FILL_RATIO = 0.8

/**
 * Scales the sheet to occupy most of the stage width. Height deliberately does not constrain
 * the scale: a short stage scrolls instead of shrinking the document until its text is unreadable.
 * Iframe layout size stays at true paper dimensions; apply the returned factor via CSS transform.
 */
export const computePackingSlipCustomPreviewPaperScale = ({
  paperWidthPx,
  paperHeightPx,
  stageClientWidth,
  stagePaddingPx = 48,
  stageFillRatio = PACKING_SLIP_CUSTOM_PREVIEW_STAGE_FILL_RATIO,
}: {
  paperWidthPx: number
  paperHeightPx: number
  stageClientWidth: number
  stagePaddingPx?: number
  stageFillRatio?: number
}): number => {
  if (!paperWidthPx || !paperHeightPx) {
    return 1
  }

  const availableWidth = Math.max(0, stageClientWidth - stagePaddingPx)
  if (!availableWidth) {
    return 1
  }

  const normalizedFillRatio =
    Number.isFinite(stageFillRatio) && stageFillRatio > 0
      ? Math.min(stageFillRatio, 1)
      : PACKING_SLIP_CUSTOM_PREVIEW_STAGE_FILL_RATIO
  const scaleByWidth = (availableWidth * normalizedFillRatio) / paperWidthPx

  if (!Number.isFinite(scaleByWidth) || scaleByWidth <= 0) {
    return 1
  }

  return scaleByWidth
}

const PACKING_SLIP_PREVIEW_BASELINE_STYLE_ID = 'mc-packing-slip-preview-baseline'

const isPackingSlipFullHtmlDocument = (html: string): boolean =>
  /^<!DOCTYPE/i.test(html) || /^<html[\s>]/i.test(html)

const buildPackingSlipPreviewBaselineCss = ({
  paperWidth,
  paperHeight,
  marginMm,
  includeBodyPadding,
}: {
  paperWidth: string
  paperHeight: string
  marginMm: number
  includeBodyPadding: boolean
}) => {
  const bodyPadding = includeBodyPadding ? `padding: ${marginMm}mm;` : ''

  return `*, *::before, *::after {
    box-sizing: border-box;
  }
  html {
    margin: 0;
    padding: 0;
    width: ${paperWidth};
    height: ${paperHeight};
    min-height: ${paperHeight};
    background: #ffffff;
  }
  body {
    margin: 0;
    box-sizing: border-box;
    width: 100%;
    min-height: 100%;
    min-height: ${paperHeight};
    display: flex;
    flex-direction: column;
    background: #ffffff;
    ${bodyPadding}
  }
  body > :only-child {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
  body > :last-child:not(script):not(style):not(link):not(noscript),
  body > :only-child > :last-child:not(script):not(style):not(link):not(noscript) {
    margin-top: auto;
  }
  .mc-packing-slip-liquid {
    display: none !important;
  }
  [data-mc-preview]::after {
    content: attr(data-mc-preview);
  }
  .mc-packing-slip-preview-only {
    display: revert !important;
  }
  tr.mc-packing-slip-preview-only {
    display: table-row !important;
  }
  [data-mc-preview-logo] > .packing-list__logo-placeholder {
    display: flex !important;
  }
  .packing-list__barcode.mc-packing-slip-preview-only {
    display: block !important;
  }
  @page {
    size: ${paperWidth} ${paperHeight};
    margin: ${marginMm}mm;
  }`
}

const injectPackingSlipPreviewViewportMeta = (
  documentHtml: string,
  paperWidthPx: number
): string => {
  const viewportTag = `<meta name="viewport" content="width=${Math.round(
    paperWidthPx
  )}, initial-scale=1">`

  if (/<meta[^>]+name=["']viewport["'][^>]*>/i.test(documentHtml)) {
    return documentHtml.replace(
      /<meta[^>]+name=["']viewport["'][^>]*>/i,
      viewportTag
    )
  }

  if (/<head[^>]*>/i.test(documentHtml)) {
    return documentHtml.replace(/<head([^>]*)>/i, `<head$1>${viewportTag}`)
  }

  if (/<html[\s>]/i.test(documentHtml)) {
    return documentHtml.replace(/<html([^>]*)>/i, `<html$1><head>${viewportTag}</head>`)
  }

  return documentHtml
}

const injectPackingSlipPreviewBaselineIntoDocument = (
  documentHtml: string,
  baselineCss: string
): string => {
  const styleTag = `<style id="${PACKING_SLIP_PREVIEW_BASELINE_STYLE_ID}">\n${baselineCss}\n</style>`

  if (new RegExp(`id=["']${PACKING_SLIP_PREVIEW_BASELINE_STYLE_ID}["']`, 'i').test(documentHtml)) {
    return documentHtml
  }

  if (/<\/head>/i.test(documentHtml)) {
    return documentHtml.replace(/<\/head>/i, `${styleTag}\n</head>`)
  }

  if (/<html[\s>]/i.test(documentHtml)) {
    return documentHtml.replace(/<html([^>]*)>/i, `<html$1><head>${styleTag}</head>`)
  }

  return documentHtml
}

/**
 * Builds isolated iframe HTML for client-side preview.
 * Liquid-style expressions are not evaluated — they render as literal text in the browser.
 */
export const buildPackingSlipCustomPreviewSrcDoc = ({
  html,
  paperWidth,
  paperHeight,
  marginMm = 0,
  paperWidthPx,
}: PackingSlipCustomPreviewDocumentOptions): string => {
  const trimmed = html.trim()
  const margin = Number.isFinite(marginMm) ? Math.max(0, marginMm) : 0
  const layoutWidthPx = Math.round(
    paperWidthPx ?? packingSlipCssLengthToPx(paperWidth)
  )

  if (isPackingSlipFullHtmlDocument(trimmed)) {
    const baselineCss = buildPackingSlipPreviewBaselineCss({
      paperWidth,
      paperHeight,
      marginMm: margin,
      includeBodyPadding: margin > 0,
    })
    const withBaseline = injectPackingSlipPreviewBaselineIntoDocument(trimmed, baselineCss)
    return injectPackingSlipPreviewViewportMeta(withBaseline, layoutWidthPx)
  }

  const fragmentBaselineCss = buildPackingSlipPreviewBaselineCss({
    paperWidth,
    paperHeight,
    marginMm: margin,
    includeBodyPadding: true,
  })

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=${layoutWidthPx}, initial-scale=1">
<style id="${PACKING_SLIP_PREVIEW_BASELINE_STYLE_ID}">
${fragmentBaselineCss}
</style>
</head>
<body>
${html}
</body>
</html>`
}
